import { FunctionResponse } from "@google/genai";
import { ADHDAPIAdapter } from "../../../lib/adhd-api-adapter";
import { OverwhelmAssessment } from "../../../lib/overwhelm-detector";
import { CollapsibleProjectTreeRef } from "../CollapsibleProjectTree";

export interface OverwhelmInterventionActions {
  collapseAllProjects: () => void;
  checkForOverwhelm: () => void;
  setAiPersonalityMode: (mode: "supportive" | "focused" | "calm") => void;
}

export class FunctionCallProcessor {
  private adhdAdapter: ADHDAPIAdapter;
  private projectTreeRef: React.RefObject<CollapsibleProjectTreeRef>;
  private actions: OverwhelmInterventionActions;

  constructor(
    adhdAdapter: ADHDAPIAdapter,
    projectTreeRef: React.RefObject<CollapsibleProjectTreeRef>,
    actions: OverwhelmInterventionActions
  ) {
    this.adhdAdapter = adhdAdapter;
    this.projectTreeRef = projectTreeRef;
    this.actions = actions;
  }

  /**
   * Handle automatic function calls for overwhelm interventions
   */
  handleAutomaticFunctionCall(
    functionCall: { name: string; args?: any },
    checkOverwhelmCallback?: () => void
  ): void {
    switch (functionCall.name) {
      case "collapse_all_projects":
        this.projectTreeRef.current?.collapseAllProjects();
        break;
      case "get_tasks_by_quadrant":
        // This would filter the current view to show only specific quadrant
        // Implementation depends on UI design - for now, just show high priority tasks
        if (checkOverwhelmCallback) {
          checkOverwhelmCallback();
        }
        break;
      case "reprioritize_all_tasks":
        this.adhdAdapter.reprioritizeAllTodos().catch(console.error);
        break;
      default:
        console.warn("Unknown automatic function call:", functionCall.name);
    }
  }

  /**
   * Generate ADHD-aware response based on function result and context
   */
  generateADHDAwareResponse(
    functionResponse: FunctionResponse,
    functionName: string,
    isSuccess: boolean,
    overwhelmAssessment: OverwhelmAssessment | null,
    aiPersonalityMode: "supportive" | "focused" | "calm"
  ): any {
    // Keep the original response structure intact
    const originalOutput = functionResponse.response?.output;

    if (!isSuccess) {
      return this.handleErrorResponse(functionResponse, functionName);
    }

    // For now, return the original output structure to avoid breaking the API
    // The enhancement can be added as an additional field if needed
    return { output: originalOutput };
  }

  private handleErrorResponse(functionResponse: FunctionResponse, functionName: string): any {
    // Check for subscription limit errors first
    const responseOutput = functionResponse.response?.output as any;
    if (
      responseOutput &&
      typeof responseOutput === "object" &&
      responseOutput.error === "SUBSCRIPTION_LIMIT_EXCEEDED"
    ) {
      return this.handleSubscriptionLimitError(responseOutput);
    }

    // Gentle, encouraging error messages for other errors
    const errorMessage = this.getErrorMessage(functionName);
    return { output: errorMessage };
  }

  private handleSubscriptionLimitError(responseOutput: any): any {
    const limitType = responseOutput.limitType || "usage";
    const currentUsage = responseOutput.currentUsage || 0;
    const limit = responseOutput.limit || 0;

    let upgradeBenefits = "";
    let upgradeMessage = "";

    switch (limitType) {
      case "projects":
        upgradeBenefits =
          "With an upgraded plan, you get unlimited projects, AI-powered organization, and advanced ADHD productivity features.";
        upgradeMessage = `🚫 You've reached your project limit (${currentUsage}/${limit}). ${upgradeBenefits} I can help you find the perfect plan - just ask me about upgrading!`;
        break;
      case "todos":
        upgradeBenefits =
          "With an upgraded plan, you get unlimited tasks, smart scheduling, AI categorization, and priority insights.";
        upgradeMessage = `🚫 You've reached your task limit (${currentUsage}/${limit}). ${upgradeBenefits} I can help you find the perfect plan - just ask me about upgrading!`;
        break;
      case "api_cost":
        upgradeBenefits =
          "With an upgraded plan, you get higher usage limits and access to premium AI features.";
        upgradeMessage = `🚫 You've reached your API usage limit (${currentUsage}/${limit}). ${upgradeBenefits} I can help you find the perfect plan - just ask me about upgrading!`;
        break;
      default:
        upgradeMessage = `🚫 You've reached a limit on your current plan. Upgrading unlocks unlimited usage and premium ADHD-focused productivity features. I can help you find the perfect plan - just ask me about upgrading!`;
    }

    return {
      output: {
        ...responseOutput,
        message: upgradeMessage,
        suggestUpgrade: true,
        upgradeCallToAction: `Ready to unlock unlimited ${limitType === "api_cost" ? "usage" : limitType}? Ask me "What are my upgrade options?" and I'll show you our ADHD-friendly plans!`,
      },
    };
  }

  private getErrorMessage(functionName: string): string {
    switch (functionName) {
      case "create_todo":
        return "That didn't work as expected, but no worries! Let's try a different approach to capture that task.";
      case "update_todo":
        return "I couldn't update that task right now. Your progress is still valuable - let's try again in a moment.";
      default:
        return "Something went sideways there, but it's totally fine! These things happen. Let's keep moving forward.";
    }
  }

  private enhanceSuccessResponse(
    baseResponse: string,
    overwhelmAssessment: OverwhelmAssessment | null,
    aiPersonalityMode: "supportive" | "focused" | "calm",
    functionResponse: FunctionResponse
  ): any {
    let enhancedMessage = baseResponse;

    // Success responses based on current overwhelm state and AI personality mode
    if (overwhelmAssessment) {
      switch (overwhelmAssessment.overallLevel) {
        case "severe":
          enhancedMessage = `${baseResponse} Take a breath - you're handling this really well despite it feeling overwhelming. One step at a time.`;
          break;
        case "moderate":
          enhancedMessage = `${baseResponse} Great progress! You're building momentum even when things feel intense.`;
          break;
        case "mild":
          enhancedMessage = `${baseResponse} Nice work staying organized!`;
          break;
      }
    }

    // Personality mode adjustments
    switch (aiPersonalityMode) {
      case "calm":
        enhancedMessage = `${enhancedMessage} Remember, you're doing great. Progress over perfection.`;
        break;
      case "focused":
        enhancedMessage = `${enhancedMessage} Excellent focus - let's keep this momentum going.`;
        break;
      case "supportive":
      default:
        // Keep as is
        break;
    }

    // Return the same structure as the original response
    return {
      output:
        typeof functionResponse.response?.output === "object"
          ? { ...functionResponse.response.output, message: enhancedMessage }
          : enhancedMessage,
    };
  }
}
